/**
 * CAPTCHA field (intake/spec §3.7, FR-7, NFR-6). Two modes:
 *
 *  - LIVE (behind CloudFront, VITE_WAF_CAPTCHA_* set): renders the AWS WAF puzzle INLINE, ON MOUNT,
 *    into the "Spam check" box (via the shared `useCaptcha` hook, which owns the container ref +
 *    status). The resident solves the puzzle as part of the form, THEN clicks submit — the pattern
 *    every site uses. Under WAF immunity the render resolves instantly with no visible puzzle → this
 *    shows a clean "✓ Verified" state. The AWS widget ships its own WCAG-friendly puzzle incl. an
 *    audio alternative; we own the surrounding label, the announced status, and an accessible retry.
 *
 *  - FALLBACK (local dev / mocks / tests, CAPTCHA disabled): a single "I'm a person" checkbox stands
 *    in so the form's required-gate stays demonstrable end-to-end without a live WAF.
 *
 * This component is presentation only — the parent supplies status/passed/error + the container ref
 * from `useCaptcha`.
 */
import { forwardRef } from "react";
import { CAPTCHA_ENABLED } from "../api/wafCaptcha.js";
import type { CaptchaStatus } from "./useCaptcha.js";

interface Props {
  /** Fallback-mode checkbox state (ignored in live mode). */
  passed: boolean;
  onChange: (passed: boolean) => void;
  id: string;
  /** Live-mode status (from useCaptcha). */
  status?: CaptchaStatus;
  /** Accessible error message shown when the render/solve fails. */
  error?: string;
  /** Retry the inline render after a failure (live mode). */
  onRetry?: () => void;
}

export const CaptchaField = forwardRef<HTMLDivElement, Props>(function CaptchaField(
  { passed, onChange, id, status = "idle", error, onRetry },
  containerRef,
): JSX.Element {
  const labelId = `${id}-label`;

  if (CAPTCHA_ENABLED) {
    const verified = status === "verified";
    const errored = status === "error";
    return (
      <div className="td-field">
        <span className="td-field__label" id={labelId}>
          Spam check
          <span className="td-field__req" aria-hidden="true">
            {" "}
            *
          </span>
        </span>
        <div className="td-captcha" role="group" aria-labelledby={labelId}>
          {/* Loading (SDK/first render in flight) — a short-lived hint before the puzzle paints. */}
          {status === "loading" ? (
            <p className="td-captcha__note">Loading the spam check…</p>
          ) : null}

          {/* The AWS WAF SDK renders the puzzle into this container on mount. Kept mounted across all
              live states so the SDK always has a stable node to paint into. */}
          <div
            ref={containerRef}
            data-testid="captcha-container"
            tabIndex={-1}
            hidden={verified}
          />

          {/* Verified — solved inline, or resolved instantly under WAF immunity. */}
          {verified ? (
            <p className="td-captcha__verified" data-testid="captcha-verified">
              <span aria-hidden="true">✓ </span>Verified — you're all set.
            </p>
          ) : null}

          {/* Announce the pass/fail state to assistive tech without stealing focus. */}
          <p className="td-sr-only" aria-live="polite">
            {verified ? "Spam check passed." : errored ? "Spam check failed." : ""}
          </p>

          {errored && error ? (
            <div className="td-captcha__error" role="alert">
              <p>{error}</p>
              {onRetry ? (
                <button type="button" className="td-btn td-btn--secondary" onClick={onRetry}>
                  Retry spam check
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // Fallback (no live WAF): checkbox gate.
  return (
    <div className="td-field">
      <span className="td-field__label" id={labelId}>
        Spam check
        <span className="td-field__req" aria-hidden="true">
          {" "}
          *
        </span>
      </span>
      <div className="td-captcha" role="group" aria-labelledby={labelId}>
        <label className="td-captcha__box" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={passed}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>I'm a person, not a bot</span>
        </label>
        <p className="td-captcha__note">
          A live CAPTCHA (with an accessible audio alternative) runs on the deployed site.
        </p>
      </div>
    </div>
  );
});
